import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  ActionKey,
  type EntryChargeConfirm,
  type EntryChargeBenefitOption,
  type EntryChargeInput,
  type EntryChargePreview,
  type EntryChargeRefund,
  type MemberChargeHistory,
  type MemberChargeReceipt,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

type Pending =
  | { kind: 'PAYMENT'; body: EntryChargeConfirm }
  | { kind: 'REFUND'; id: string; body: EntryChargeRefund };
export default function EntryCashierPanel({
  memberId,
  memberName,
  tournamentId,
  onClose,
}: {
  memberId: string;
  memberName: string;
  tournamentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('tournament');
  const client = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const user = useAuthStore((s) => s.user);
  const can = (action: string) => !!user?.isSystemUser || !!actions?.includes(action);
  // Per-tab recovery survives navigation/reload; server still validates identity and payload.
  const recoveryKey = `member-charge-request:${user?.id}:${memberId}:${tournamentId}`;
  const [pending, setPending] = useState<Pending | null>(() => {
    try {
      const value = JSON.parse(sessionStorage.getItem(recoveryKey) ?? 'null') as Pending | null;
      if (
        value &&
        ['PAYMENT', 'REFUND'].includes(value.kind) &&
        typeof value.body?.operationKey === 'string'
      )
        return value;
    } catch {
      /* Corrupt local data is not an authoritative request. */
    }
    return null;
  });
  const [input, setInput] = useState<EntryChargeInput>(
    pending?.kind === 'PAYMENT'
      ? {
          tournamentId: pending.body.tournamentId,
          operation: pending.body.operation,
          tender: pending.body.tender,
          ...(pending.body.benefit ? { benefit: pending.body.benefit } : {}),
        }
      : { tournamentId, operation: 'ENTRY', tender: 'CASH' },
  );
  const [benefits, setBenefits] = useState<EntryChargeBenefitOption[]>([]);
  const [preview, setPreview] = useState<EntryChargePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState<MemberChargeReceipt | null>(null);
  const [reason, setReason] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const generation = useRef(0);
  const history = useQuery({
    queryKey: ['member-charges', memberId, tournamentId, cursor],
    queryFn: () =>
      api.get<MemberChargeHistory>(`/members/${memberId}/charges`, {
        params: { tournamentId, ...(cursor ? { cursor } : {}) },
      }),
    enabled: can(ActionKey.MEMBER_CHARGE_READ),
    retry: false,
  });
  const locked = busy || !!pending;
  function change(next: EntryChargeInput) {
    generation.current++;
    setInput(next);
    setPreview(null);
    setError('');
    setCompleted(null);
  }
  async function loadPreview() {
    const revision = ++generation.current;
    setBusy(true);
    setError('');
    setPreview(null);
    setCompleted(null);
    try {
      const result = await api.post<EntryChargePreview>(
        `/members/${memberId}/charges/preview`,
        input,
      );
      if (revision === generation.current) {
        setPreview(result);
        setBenefits(result.benefits ?? []);
      }
    } catch {
      if (revision === generation.current) setError('loadError');
    } finally {
      setBusy(false);
    }
  }
  async function submit(request: Pending) {
    if (busy) return;
    try {
      sessionStorage.setItem(recoveryKey, JSON.stringify(request));
    } catch {
      setError('recoveryUnavailable');
      return;
    }
    setPending(request);
    setBusy(true);
    setError('');
    setCompleted(null);
    try {
      const path = request.kind === 'PAYMENT' ? 'confirm' : `${request.id}/refund`;
      const receipt = await api.post<MemberChargeReceipt>(
        `/members/${memberId}/charges/${path}`,
        request.body,
      );
      sessionStorage.removeItem(recoveryKey);
      setPending(null);
      setPreview(null);
      setReason('');
      setCompleted(receipt);
      void client.invalidateQueries({ queryKey: ['member-charges', memberId, tournamentId] });
      void client.invalidateQueries({ queryKey: ['tournament', tournamentId] });
      void client.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      // Only a definitive rejection permits a new key. Ambiguous transport/server failures
      // keep the exact request and freeze editing until the original operation is recovered.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        sessionStorage.removeItem(recoveryKey);
        setPending(null);
        setPreview(null);
        setError(status === 409 ? 'stale' : 'rejected');
      } else setError('uncertain');
    } finally {
      setBusy(false);
    }
  }
  function receiptDetails(receipt: MemberChargeReceipt) {
    return (
      <div className="text-sm space-y-1">
        {receipt.operation && (
          <p>
            {t('cashier.operation')}: {t(`cashier.${receipt.operation}`)}
          </p>
        )}
        <p>
          {t('cashier.principal')}: {receipt.principalAmount} · {t('cashier.venueFee')}:{' '}
          {receipt.venueFeeAmount}
        </p>
        <p>
          {t('cashier.reduction')}: {receipt.reductionAmount}
          {receipt.venueFeeDue !== undefined && (
            <>
              {' '}
              · {t('cashier.venueDue')}: {receipt.venueFeeDue}
            </>
          )}
        </p>
        {(receipt.feeVersionId || receipt.benefit) && (
          <details>
            <summary>{t('cashier.priceReferences')}</summary>
            {receipt.feeVersionId && (
              <p className="break-all">
                {t('cashier.feeVersion')}: {receipt.feeVersionId}
              </p>
            )}
            {receipt.benefit && (
              <p className="break-all">
                {t(`cashier.${receipt.benefit.kind}`)}: {receipt.benefit.id} ·{' '}
                {t('cashier.benefitUse')}: {receipt.benefit.useId}
              </p>
            )}
          </details>
        )}
      </div>
    );
  }
  const canPay =
    !!preview?.postingAvailable &&
    !!preview.quoteHash &&
    !!preview.expiresAt &&
    Date.parse(preview.expiresAt) > Date.now() &&
    preview.blockers.length === 0 &&
    can(ActionKey.MEMBER_CHARGE_CONFIRM);
  return (
    <section
      className="mb-6 rounded-xl border border-base-300 p-4 space-y-4"
      aria-label={t('cashier.title')}
    >
      <div className="flex justify-between gap-3">
        <h2 className="font-bold">
          {t('cashier.title')} — {memberName}
        </h2>
        <button type="button" disabled={locked} onClick={onClose}>
          {t('cashier.close')}
        </button>
      </div>
      <p className="text-sm">{t('cashier.description')}</p>
      <div className="flex flex-wrap gap-4">
        <label>
          {t('cashier.operation')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.operation}
            disabled={locked}
            onChange={(e) => {
              setBenefits([]);
              change({
                tournamentId,
                tender: input.tender,
                operation: e.target.value as EntryChargeInput['operation'],
              });
            }}
          >
            {(['ENTRY', 'REENTRY', 'REBUY', 'ADDON'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`cashier.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('cashier.tender')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.tender}
            disabled={locked}
            onChange={(e) =>
              change({ ...input, tender: e.target.value as EntryChargeInput['tender'] })
            }
          >
            <option value="CASH">{t('cashier.CASH')}</option>
            <option value="ZIPA">ZIPA</option>
          </select>
        </label>
      </div>
      {(benefits.length > 0 || input.benefit) && (
        <label className="block">
          {t('cashier.benefit')}
          <select
            className="block rounded border p-2 bg-base-100"
            value={input.benefit ? `${input.benefit.kind}:${input.benefit.id}` : ''}
            disabled={locked}
            onChange={(event) => {
              const selected = benefits.find(
                (option) => `${option.kind}:${option.id}` === event.target.value,
              );
              change({
                ...input,
                benefit: selected ? { kind: selected.kind, id: selected.id } : undefined,
              });
            }}
          >
            <option value="">{t('cashier.noBenefit')}</option>
            {input.benefit &&
              !benefits.some(
                (option) => option.kind === input.benefit!.kind && option.id === input.benefit!.id,
              ) && (
                <option value={`${input.benefit.kind}:${input.benefit.id}`} disabled>
                  {t('cashier.BENEFIT_UNAVAILABLE')}
                </option>
              )}
            {benefits.map((option) => (
              <option
                key={`${option.kind}:${option.id}`}
                value={`${option.kind}:${option.id}`}
                disabled={!!option.blocker}
              >
                {t(`cashier.${option.kind}`)} · {option.label} ·{' '}
                {option.blocker ? t(`cashier.${option.blocker}`) : option.eligibleAmount}
              </option>
            ))}
          </select>
          <span className="text-sm">{t('cashier.benefitHelp')}</span>
        </label>
      )}
      <button
        type="button"
        className="rounded bg-primary text-primary-content p-2"
        disabled={locked}
        onClick={() => void loadPreview()}
      >
        {t('cashier.preview')}
      </button>
      {error && (
        <p role="alert" className="text-error">
          {t(`cashier.${error}`)}
        </p>
      )}
      {preview && (
        <div className="space-y-2">
          <p>
            {t('cashier.principal')}: {preview.principalAmount ?? '—'}
          </p>
          {preview.pricing && (
            <div className="text-sm space-y-1">
              <p>
                {t('cashier.ordinaryPrice')}: {preview.pricing.ordinaryVenueFeeAmount}
              </p>
              {preview.pricing.reservationVenueFeeAmount !== null && (
                <p>
                  {t('cashier.reservationPrice')}: {preview.pricing.reservationVenueFeeAmount}
                </p>
              )}
              {preview.pricing.afterBreakVenueFeeAmount !== null && (
                <p>
                  {t('cashier.afterBreakPrice')}: {preview.pricing.afterBreakVenueFeeAmount}
                </p>
              )}
              <p>{t(`cashier.${preview.pricing.phase}`)}</p>
            </div>
          )}
          {preview.amounts ? (
            <>
              <p>
                {t('cashier.venueFee')}: {preview.amounts.selectedVenueFeeAmount}
              </p>
              <p>
                {t('cashier.reduction')}: {preview.amounts.ticketReduction}
              </p>
              <p>
                {t('cashier.venueDue')}: {preview.amounts.venueFeeDue}
              </p>
              <p className="font-bold">
                {t(preview.postingAvailable ? 'cashier.total' : 'cashier.estimate')}:{' '}
                {preview.amounts.totalDue}
              </p>
              <p>
                {preview.amounts.reasons
                  .map((code) => t(`cashier.${code}`, { defaultValue: t('cashier.priceRule') }))
                  .join(' / ')}
              </p>
            </>
          ) : (
            <p>{t('cashier.priceUnavailable')}</p>
          )}
          {preview.blockers.map((code) => (
            <p key={code}>
              {t(`cashier.${code}`, { defaultValue: t('cashier.CHARGE_INTEGRATION_UNAVAILABLE') })}
            </p>
          ))}
          <button
            type="button"
            className="rounded bg-primary text-primary-content p-2"
            disabled={locked || !canPay}
            onClick={() => {
              if (preview.expiresAt && Date.parse(preview.expiresAt) <= Date.now()) {
                setPreview(null);
                setError('stale');
                return;
              }
              if (canPay && preview.quoteHash)
                void submit({
                  kind: 'PAYMENT',
                  body: {
                    ...input,
                    quoteHash: preview.quoteHash,
                    operationKey: crypto.randomUUID(),
                  },
                });
            }}
          >
            {t('cashier.pay')}
          </button>
        </div>
      )}
      {pending && !busy && (
        <button type="button" onClick={() => void submit(pending)}>
          {t('cashier.retry')}
        </button>
      )}
      {completed && (
        <div role="status">
          <p>
            {t('cashier.saved')} {completed.id} · {completed.netDue} · {completed.tender}
          </p>
          {receiptDetails(completed)}
        </div>
      )}
      <h3 className="font-semibold">{t('cashier.receipts')}</h3>
      <p className="text-sm">{t('cashier.historyNote')}</p>
      {history.isPending && <p>{t('cashier.loading')}</p>}
      {history.isError && <p role="alert">{t('cashier.loadError')}</p>}
      {history.data?.items.length === 0 && <p>{t('cashier.empty')}</p>}
      {history.data?.items.map((row) => (
        <article key={row.id} className="border rounded p-3 space-y-2">
          <p className="break-all">{row.id}</p>
          <p>
            {row.netDue} · {row.tender ? t(`cashier.${row.tender}`) : '—'} ·{' '}
            {t(`cashier.${row.status}`, { defaultValue: row.status })}
          </p>
          {receiptDetails(row)}
          <p>
            {t('cashier.paidAt')}:{' '}
            {row.paidAt
              ? new Date(row.paidAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })
              : '—'}
          </p>
          {row.correctedAt && (
            <p>
              {t('cashier.correctedAt')}:{' '}
              {new Date(row.correctedAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}
            </p>
          )}
          {row.purpose === 'ENTRY' &&
            row.status === 'POSTED' &&
            !row.correctedAt &&
            can(ActionKey.MEMBER_CHARGE_REFUND) && (
              <button
                type="button"
                disabled={locked || !row.refundable || !reason.trim()}
                onClick={() =>
                  void submit({
                    kind: 'REFUND',
                    id: row.id,
                    body: { reason: reason.trim(), operationKey: crypto.randomUUID() },
                  })
                }
              >
                {t('cashier.refund')}
              </button>
            )}
        </article>
      ))}
      {can(ActionKey.MEMBER_CHARGE_REFUND) && (
        <label className="block">
          {t('cashier.refundReason')}
          <input
            className="block border rounded p-2 bg-base-100"
            value={reason}
            maxLength={500}
            disabled={locked}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="block text-sm">{t('cashier.refundNote')}</span>
        </label>
      )}
      {(cursor || history.data?.nextCursor) && (
        <div className="flex gap-4">
          <button disabled={locked || !cursor} onClick={() => setCursor(null)}>
            {t('cashier.firstPage')}
          </button>
          <button
            disabled={locked || !history.data?.nextCursor}
            onClick={() => setCursor(history.data?.nextCursor ?? null)}
          >
            {t('cashier.nextPage')}
          </button>
        </div>
      )}
    </section>
  );
}
